/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { Scrollbar, Keyboard, Navigation, Pagination } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import { Box } from "@mui/system";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useState } from "react";
import { MOBLIE_VIEW_WIDTH } from "../../shared/constants/constants";
import AddCircularIcon from "../../shared/components/reusableComp/AddCircularIcon";
import Markdown from "markdown-to-jsx";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { v4 } from "uuid";
import { CenteredTextWithUnderline } from "../../shared/components/reusableComp/CenteredTextWithUnderline";

const quickLinkBox = (matchesMob) => css`
  &:hover {
    transform: scale(1.01);
    transition: 0.7s ease;
    ${matchesMob && "opacity: 1;"}
  }
  width: auto;
  height: 100%;
  background-color: #9b9b9b;
  position: relative;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  ${matchesMob && "opacity: 0.5;"}
`;
const descriptionWrapper = css`
  font-size: 14px;
  line-height: 35px;
`;
const wrapperBox = css`
  width: 67%;
  height: 100%;
  .swiper .swiper-button-prev {
    left: 5px;
    color: aliceblue;
    font-weight: 800;
    overflow: visible;
  }
  .swiper .swiper-button-next {
    right: 5px;
    color: aliceblue;
    font-weight: 800;
  }
  .swiper-button-next-unique {
    top: -310px !important;
  }
  .swiper-button-prev-unique {
    top: -310px !important;
  }
`;
const accordianSummaryStyle = css`
  background-color: #1f1c1c;
  color: white;
  font-size: 16px;
  display: flex;
  justify-content: space-between;
`;
const cardWrapper = (direction, isImageRequiredOnRight) => css`
  background: #121212;
  .MuiBox-root {
    display: flex;
    align-items: center;
    img {
      width: ${direction === "row" ? "50%" : "100%"};
      order: ${direction === "row" && isImageRequiredOnRight === true ? 2 : -1};
    }
    .MuiCardContent-root {
      padding: 0;
      margin: 20px 0;
      width: ${direction === "row" ? "50%" : "100%"};
      .MuiTypography-root {
        margin: ${direction === "row" ? "30px 80px" : "10px 20px"};
        color: white;
      }
    }
  }
`;

const renderCard = (
  imgUrl,
  imgName,
  title,
  description,
  direction,
  isImageRequiredOnRight
) => {
  return (
    <Card css={() => cardWrapper(direction, isImageRequiredOnRight)} key={v4()}>
      <Box sx={{ display: "flex", flexDirection: direction }}>
        <CardMedia
          component="img"
          image={`${process.env.REACT_APP_BASEURL}${imgUrl}`}
          alt={imgName}
        />
        <CardContent>
          <Typography gutterBottom variant="h5" component="div">
            {title}
          </Typography>
          <Typography
            css={descriptionWrapper}
            variant="body2"
            color="text.secondary"
          >
            <Markdown>{description || ""}</Markdown>
          </Typography>
        </CardContent>
      </Box>
    </Card>
  );
};
export default function MultipleSlides({
  industryCardData,
  cardsPerView,
  title,
  description,
}) {
  const matches = useMediaQuery(MOBLIE_VIEW_WIDTH);
  const [visible, setVisible] = useState(false);
  const [value, setValue] = useState(-1);

  const handleHover = (index) => {
    setVisible(true);
    setValue(index);
  };

  return (
    <Grid>
      {matches && (
        <Box
          css={{
            display: "flex",
            height: `${
              title ||
              description ||
              (industryCardData && industryCardData.length)
                ? "75vh"
                : "unset"
            }`,
          }}
          m={2}
        >
          <Box css={{ width: "26.3%" }} p={1} pr={8}>
            <Typography
              gutterBottom
              variant="h5"
              color="white"
              width="35vw"
              css={{ wordBreak: "break-word" }}
            >
              {title}
            </Typography>
            <Typography gutterBottom variant="body2" color="text.secondary">
              <Markdown css={descriptionWrapper}>{description || ""}</Markdown>
            </Typography>
          </Box>
          <Box p={industryCardData.length < 4 && 1} css={wrapperBox}>
            <Swiper
              modules={[Scrollbar, Keyboard, Navigation, Pagination]}
              grabCursor
              navigation={
                matches
                  ? {
                      clickable: true,
                      nextEl: ".swiper-button-next-unique",
                      prevEl: ".swiper-button-prev-unique",
                    }
                  : false
              }
              keyboard={{ enabled: true }}
              pagination={
                !matches
                  ? {
                      el: ".custom-pagination-div",
                      clickable: true,
                      type: "bullets",
                    }
                  : false
              }
              spaceBetween={4}
              slidesPerView={
                matches ? cardsPerView : industryCardData.length < 4 ? 3 : 3.3
              }
              style={{ height: "100%" }}
            >
              {industryCardData?.map((item, index) => (
                <SwiperSlide key={index} style={{ height: "100%" }}>
                  <div style={{ height: "60%" }}>
                    <Box
                      css={() => quickLinkBox(matches)}
                      p={matches ? 3 : 2}
                      style={{
                        backgroundImage: `url(${
                          process.env.REACT_APP_BASEURL +
                          item?.media?.data?.attributes?.url
                        })`,
                      }}
                      onMouseEnter={() => handleHover(index)}
                      onMouseLeave={() => setVisible(false)}
                      onClick={() =>
                        !matches &&
                        (window.location = "/trucks" + item.buttonLink)
                      }
                    >
                      {matches && visible && value === index && (
                        <AddCircularIcon
                          buttonCss="position: relative; top: 90%; font-size: small;"
                          handleClick={() =>
                            item?.buttonLink &&
                            (window.location = "/trucks" + item.buttonLink)
                          }
                          buttonText={item?.buttonText}
                          iconCss={{
                            width: "18px",
                            height: "18px",
                            marginRight: "8px",
                            padding: "4px",
                          }}
                        />
                      )}
                    </Box>
                    <>
                      <Typography
                        style={{ opacity: "1", color: "#ffffff" }}
                        variant={"h6"}
                      >
                        {item?.title}
                      </Typography>
                      <Typography
                        css={descriptionWrapper}
                        variant="body2"
                        color="text.secondary"
                      >
                        <Markdown>{item.description || ""}</Markdown>
                      </Typography>
                    </>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
            {!matches && <div className="custom-pagination-div" />}
            {matches && (
              <div className="swiper-button-prev-unique">
                <ArrowBackIosIcon style={{ fontSize: "50px" }} />
              </div>
            )}
            {matches && (
              <div className="swiper-button-next-unique">
                <ArrowForwardIosIcon style={{ fontSize: "50px" }} />
              </div>
            )}
          </Box>
        </Box>
      )}
      {!matches && (
        <Box mt={4} px={2} mb={3}>
          <CenteredTextWithUnderline
            title={title}
            description={description}
            bottomMargin="5%"
            titleVariant="h5"
            titleColor="white"
            mobileUnderline="yes"
          />
          {industryCardData.map((item, index) => (
            <Accordion key={index} disableGutters={true}>
              <AccordionSummary
                css={accordianSummaryStyle}
                expandIcon={<ExpandMoreIcon style={{ color: "white" }} />}
              >
                <div
                  style={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <div>{item?.title}</div>
                  <div
                    style={{ marginRight: "10px" }}
                    onClick={() =>
                      item?.buttonLink &&
                      (window.location = "/trucks" + item.buttonLink)
                    }
                  >
                    {item?.buttonText}
                  </div>
                </div>
              </AccordionSummary>
              <AccordionDetails
                style={{
                  backgroundColor: "#1f1c1c",
                  color: "white",
                  padding: 0,
                }}
              >
                {renderCard(
                  item?.media?.data?.attributes?.url,
                  item?.media?.data?.attributes?.name,
                  item?.title,
                  item?.description,
                  matches ? "row" : "column"
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </Grid>
  );
}

MultipleSlides.defaultProps = {
  industryCardData: [],
  cardsPerView: 5,
};
